import React from 'react';
import { Link } from 'react-router-dom';
import { FiInbox, FiExternalLink, FiArrowRight } from 'react-icons/fi';

interface Props {
  title?: string;
  instructions?: string[];
  docsUrl?: string;
  actionLabel?: string;
  actionPath?: string;
  compact?: boolean;
}

/**
 * EmptyWidgetState - Displays when a widget has no data
 * Shows inline instructions on how to get data
 */
const EmptyWidgetState: React.FC<Props> = ({
  title = 'No Data Available',
  instructions = [],
  docsUrl,
  actionLabel,
  actionPath,
  compact = false
}) => {
  if (compact) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-3">
        <FiInbox className="w-6 h-6 text-gray-300 dark:text-gray-600 mb-2" />
        <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
        {actionPath && (
          <Link
            to={actionPath}
            className="text-xs text-blue-500 hover:text-blue-600 mt-1 flex items-center gap-1"
          >
            {actionLabel || 'Set up'}
            <FiArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-4">
      <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
        <FiInbox className="w-6 h-6 text-gray-400 dark:text-gray-500" />
      </div>

      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {title}
      </h4>

      {instructions.length > 0 && (
        <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-3 text-left max-w-xs">
          {instructions.map((instruction) => (
            <li key={instruction} className="flex items-start gap-2">
              <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-medium mt-0.5">
                {instructions.indexOf(instruction) + 1}
              </span>
              <span>{instruction}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3">
        {actionPath && (
          <Link
            to={actionPath}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
          >
            {actionLabel || 'Get Started'}
            <FiArrowRight className="w-3 h-3" />
          </Link>
        )}

        {docsUrl && (
          <a
            href={docsUrl}
            target={docsUrl.startsWith('http') ? '_blank' : undefined}
            rel={docsUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <FiExternalLink className="w-3 h-3" />
            Documentation
          </a>
        )}
      </div>
    </div>
  );
};

export default EmptyWidgetState;
